import { useEffect, useRef } from "react";
import { getDocs, query, where, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { getMessagingInstance, getToken, onMessage, dbUsers } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { Capacitor } from "@capacitor/core";
import { PushNotifications, Token, PushNotificationSchema } from "@capacitor/push-notifications";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/** Store an FCM token in the user's Firestore document, removing it from any other user first */
async function saveTokenToFirestore(uid: string, token: string): Promise<void> {
  const allUsersSnap = await getDocs(dbUsers);
  for (const userDoc of allUsersSnap.docs) {
    const data = userDoc.data();
    if (data.userId !== uid && Array.isArray(data.fcmTokens) && data.fcmTokens.includes(token)) {
      await updateDoc(userDoc.ref, { fcmTokens: arrayRemove(token) });
    }
  }
  const usersSnap = await getDocs(query(dbUsers, where("userId", "==", uid)));
  if (!usersSnap.empty) {
    await updateDoc(usersSnap.docs[0].ref, { fcmTokens: arrayUnion(token) });
  }
}

/** Only register FCM tokens on production domain or native — prevents duplicate notifications from preview/staging sites */
const PROD_HOSTS = ["onyxlife.app", "www.onyxlife.app"];
function isProductionOrigin(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  return PROD_HOSTS.includes(window.location.hostname);
}

const usePushNotifications = (): void => {
  const userContext = useUser();
  const userDetails = userContext?.userDetails ?? null;
  const { addNotification } = useNotifications();
  const { settings } = useUserSettings();
  const initialized = useRef(false);
  const prevNotifSetting = useRef(settings.notifications);

  useEffect(() => {
    // Reset initialized flag when user toggles notifications off→on
    if (settings.notifications && !prevNotifSetting.current) {
      initialized.current = false;
    }
    prevNotifSetting.current = settings.notifications;

    if (!settings.notifications) return;
    if (!userDetails?.user?.uid) return;
    if (initialized.current) return;
    if (!isProductionOrigin()) {
      console.info("[Push] Skipping FCM registration — not production origin");
      return;
    }

    const uid = userDetails.user.uid;

    // ── Native Android/iOS: use Capacitor Push Notifications ──
    if (Capacitor.isNativePlatform()) {
      initialized.current = true;

      (async () => {
        try {
          const permResult = await PushNotifications.requestPermissions();
          if (permResult.receive !== "granted") return;

          await PushNotifications.register();

          PushNotifications.addListener("registration", async (tokenResult: Token) => {
            await saveTokenToFirestore(uid, tokenResult.value);
          });

          PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
            addNotification(
              notification.title || "Notification",
              notification.body || "",
              "announcement"
            );
          });
        } catch (err) {
          console.error("[Push] Native push notification setup failed:", err);
        }
      })();

      return;
    }

    // ── Web: use Firebase Cloud Messaging ──
    initialized.current = true;

    (async () => {
      try {
        if (!("Notification" in window)) return;
        if (!("serviceWorker" in navigator)) return;

        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        if (!VAPID_KEY) {
          console.warn("[Push] REACT_APP_FIREBASE_VAPID_KEY not set — skipping FCM token registration");
          return;
        }

        // Register the service worker if not already registered
        let swReg = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
        if (!swReg) {
          swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
          await navigator.serviceWorker.ready;
        }

        // Wait for messaging to be available (async init)
        let messaging = getMessagingInstance();
        if (!messaging) {
          // Give the async isSupported() check time to complete
          await new Promise((r) => setTimeout(r, 1500));
          messaging = getMessagingInstance();
        }
        if (!messaging) {
          console.warn("[Push] Firebase Messaging not supported in this browser");
          return;
        }

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swReg,
        });

        if (token) {
          await saveTokenToFirestore(uid, token);
          console.info("[Push] FCM token registered successfully");
        }

        // Listen for foreground push messages — show both in-app and system notification
        onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          if (title && body) {
            addNotification(title, body, "announcement");

            // Also show a system/browser notification so it appears in the notification tray
            if (Notification.permission === "granted") {
              const n = new Notification(title, {
                body,
                icon: "/icons/icon-192x192.png",
                badge: "/icons/icon-72x72.png",
                data: payload.data || {},
              });
              n.onclick = () => {
                window.focus();
                n.close();
              };
            }
          }
        });
      } catch (err) {
        console.error("[Push] Web push notification setup failed:", err);
      }
    })();
  }, [userDetails, settings.notifications, addNotification]);
};

export default usePushNotifications;
